import { useState } from 'react';
import { toast } from 'sonner';
import { BookOpenText, Camera, Loader2, Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import { CreditPill } from '../../AIStudioShared';
import { addStoryScene, errMsg, shootStory } from '../../../lib/aiStudioApi';
import { cn } from '../../../lib/utils';
import { StorySceneCard } from './StorySceneCard';

/** The AI-written story, scene by scene: edit anything, then turn the whole story (or one scene) into shots. */
export function StoryEditor({ project, catalog, onChanged }) {
  const story = project.story || {};
  const scenes = story.scenes || [];
  const eps = project.format === 'series' ? project.episodes_count || 1 : 1;
  const [ep, setEp] = useState(1);
  const busy = project.direction?.status === 'running';
  if (!scenes.length && story.status !== 'writing') return null;
  const mine = scenes.filter((s) => s.episode === ep);
  const spe = story.settings?.scenes_per_episode || 20;
  const calls = Math.ceil(spe / (catalog.limits?.shots_per_call || 8));
  const hasShots = (project.scenes || []).length > 0;
  const shootAll = async () => {
    if (hasShots && !window.confirm('Turn the whole story into a fresh shot list? This replaces the current shots of every episode.')) return;
    try { await shootStory(project.id, {}); toast.success('Turning your story into shots'); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    try { await addStoryScene(project.id, { episode: ep, after_id: mine[mine.length - 1]?.id || '' }); toast.success('Scene added — edit it below'); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4 rounded-xl border border-amber-400/20 bg-neutral-900/40 p-5" data-testid="story-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-base font-semibold text-neutral-100"><BookOpenText size={16} className="text-amber-300" /> The story · {scenes.length} scenes</div>
          <p className="text-xs text-neutral-400">Edit any scene — its type, place, time, weather, mood, blocking, wardrobe, props and sound flow into its shots for free.</p>
        </div>
        <div className="flex items-center gap-2">
          <CreditPill cost={eps * calls} label="to shoot" data-testid="story-shoot-credit" />
          <Button onClick={shootAll} disabled={busy || story.status === 'writing' || !scenes.length} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="story-shoot-all-btn">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Camera size={14} className="mr-1" />} {hasShots ? 'Re-shoot the whole story' : 'Shoot this story → shots'}
          </Button>
        </div>
      </div>
      {eps > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: eps }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" onClick={() => setEp(n)} data-testid={`story-ep-${n}`}
              className={cn('rounded-md px-2.5 py-1 text-xs transition-colors', ep === n ? 'bg-amber-500 text-neutral-900' : 'bg-neutral-900 text-neutral-300 hover:text-neutral-100')}>
              EP{n} <span className="opacity-70">({scenes.filter((s) => s.episode === n).length})</span>
            </button>
          ))}
        </div>
      )}
      {story.status === 'writing' && !mine.length && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-neutral-800 p-6 text-sm text-neutral-300"><Loader2 size={14} className="animate-spin" /> The AI is writing this episode's scenes…</div>
      )}
      <div className="space-y-3">
        {mine.map((sc, i) => (
          <StorySceneCard key={sc.id} project={project} catalog={catalog} scene={sc} number={i + 1} first={i === 0} last={i === mine.length - 1} busy={busy} onChanged={onChanged} />
        ))}
      </div>
      <Button size="sm" variant="outline" disabled={busy} onClick={add} className="border-neutral-700 text-neutral-200" data-testid="story-add-scene-btn"><Plus size={13} className="mr-1" /> Add a scene</Button>
    </div>
  );
}
